import { Card, CardContent, CardHeader } from "@/components/ui/card";
import { Badge } from "@/components/ui/badge";
import { Alert, AlertDescription, AlertTitle } from "@/components/ui/alert";
import { Button } from "@/components/ui/button";
import * as LucideIcons from "lucide-react";
import { InstallationPhase } from "@/utils/installationPhases";
import { ProductCardGrid } from "./ProductCardGrid";
import { InfoIcon, AlertTriangle, AlertCircle, Info } from "lucide-react";
import type { CategoryConfig } from "@/utils/categoryMeasurementMapping";
import { TierSelector } from "./TierSelector";
import { usePhaseTierDefaultsForPhase } from "@/hooks/usePhaseTierDefaults";
import { useState, useEffect } from "react";
import { toast } from "sonner";
import { cn } from "@/lib/utils";

interface Product {
  id: string;
  name: string;
  brand?: string;
  category: string;
  unit: string;
  image_url?: string;
  customer_description?: string;
  selling_points?: string[];
  install_phase?: string;
}

interface ProductPrice {
  product_id: string;
  unit_price: number;
}

interface MeasurementVars {
  area_sqft: number;
  eave_lf: number;
  rake_lf: number;
  valley_lf: number;
  hip_lf: number;
  ridge_lf: number;
  sidewall_lf: number;
  headwall_lf: number;
  flashing_lf: number;
  chimney_count: number;
  skylight_count: number;
  pipe_1_5_count: number;
  pipe_3_count: number;
  layers: number;
}

interface LineItem {
  id?: string;
  product_id: string;
  description: string;
  calculation_source?: string;
}

interface InstallPhaseStepProps {
  phase: InstallationPhase;
  products: Product[];
  productPrices: ProductPrice[];
  selectedProductIds: Set<string>;
  onProductSelect: (product: Product, price: number) => void;
  priceBookId: string;
  measurementVars?: MeasurementVars;
  lineItems?: LineItem[];
  categoryConfigs?: CategoryConfig[];
  selectedShingleType?: string | null;
  onSkipColorSelection?: () => void;
  defaultProductId?: string | null;
  estimateId?: string;
  onTierChange?: (tier: 'good' | 'better' | 'best', phaseKey: string) => void;
}

export function InstallPhaseStep({
  phase,
  products,
  productPrices,
  selectedProductIds,
  onProductSelect,
  priceBookId,
  measurementVars,
  lineItems = [],
  categoryConfigs = [],
  selectedShingleType,
  onSkipColorSelection,
  defaultProductId,
  estimateId,
  onTierChange,
}: InstallPhaseStepProps) {
  // Get the icon component
  const IconComponent = (LucideIcons as any)[phase.icon] || LucideIcons.Circle;

  // Fetch tier defaults for this phase
  const { data: tierDefaults, isLoading: tierDefaultsLoading } = usePhaseTierDefaultsForPhase(phase.key);
  
  // Track current tier selection
  const [currentTier, setCurrentTier] = useState<'good' | 'better' | 'best'>('good');

  // Detect which tier is currently selected based on line items
  useEffect(() => {
    if (!tierDefaults || tierDefaults.length === 0) return;
    
    // Find which product from this phase is in the line items
    const currentPhaseLineItem = lineItems.find(item => {
      const product = products.find(p => p.id === item.product_id);
      return product?.install_phase === phase.key;
    });

    if (currentPhaseLineItem) {
      // Match to tier
      const matchedTier = tierDefaults.find(td => td.product_id === currentPhaseLineItem.product_id);
      if (matchedTier) {
        setCurrentTier(matchedTier.tier as 'good' | 'better' | 'best');
      }
    } else {
      // No selection yet, use default tier
      const defaultTier = tierDefaults.find(td => td.is_default);
      if (defaultTier) {
        setCurrentTier(defaultTier.tier as 'good' | 'better' | 'best');
      }
    }
  }, [tierDefaults, lineItems, phase.key, products]);

  // Handle tier change
  const handleTierChange = async (newTier: 'good' | 'better' | 'best') => {
    if (!tierDefaults || !onTierChange) return;

    const tierDefault = tierDefaults.find(td => td.tier === newTier);
    if (!tierDefault || !tierDefault.product) {
      toast.error(`No ${newTier} tier product configured for ${phase.title}`);
      return;
    }

    // Get price for the new product
    const price = productPrices.find(pp => pp.product_id === tierDefault.product_id);
    if (!price) {
      toast.error(`No price found for ${tierDefault.product.name}`);
      return;
    }

    setCurrentTier(newTier);
    onTierChange(newTier, phase.key);
    
    // Call the product select handler to swap the product
    onProductSelect(tierDefault.product as any, price.unit_price);
  };

  // Calculate price difference between tiers
  const getPriceDifference = (targetTier: 'good' | 'better' | 'best') => {
    if (!tierDefaults || tierDefaults.length < 2) return null;

    const currentTierProduct = tierDefaults.find(td => td.tier === currentTier);
    const targetTierProduct = tierDefaults.find(td => td.tier === targetTier);

    if (!currentTierProduct || !targetTierProduct) return null;

    const currentPrice = productPrices.find(pp => pp.product_id === currentTierProduct.product_id);
    const targetPrice = productPrices.find(pp => pp.product_id === targetTierProduct.product_id);

    if (!currentPrice || !targetPrice) return null;

    // Simple unit price comparison (quantity calculations are handled elsewhere)
    return targetPrice.unit_price - currentPrice.unit_price;
  };

  // Only show tier selector if we have tier defaults configured
  const showTierSelector = tierDefaults && tierDefaults.length >= 2;

  // Calculate measurement-based warnings
  const getMeasurementWarning = () => {
    if (!measurementVars) return null;

    const totalPipes = (measurementVars.pipe_1_5_count || 0) + (measurementVars.pipe_3_count || 0);
    
    // Phase 9: Penetrations (pipes, skylights, chimneys)
    if (phase.number === 9 && totalPipes > 0) {
      const selectedPipes = lineItems.filter(item => 
        item.description.toLowerCase().includes('pipe') || 
        item.description.toLowerCase().includes('boot')
      ).length;
      
      if (selectedPipes < totalPipes) {
        return `You have ${totalPipes} pipe(s) in measurements. ${selectedPipes} selected. Select up to ${totalPipes} pipe flashing products.`;
      }
    }

    if (phase.number === 9 && measurementVars.skylight_count > 0) {
      const selectedSkylights = lineItems.filter(item => 
        item.description.toLowerCase().includes('skylight')
      ).length;
      
      if (selectedSkylights < measurementVars.skylight_count) {
        return `You have ${measurementVars.skylight_count} skylight(s) in measurements. ${selectedSkylights} selected.`;
      }
    }

    // Phase 8: Flashings & Walls (chimneys)
    if (phase.number === 8 && measurementVars.chimney_count > 0) {
      const selectedChimneys = lineItems.filter(item => 
        item.description.toLowerCase().includes('chimney')
      ).length;
      
      if (selectedChimneys < measurementVars.chimney_count) {
        return `You have ${measurementVars.chimney_count} chimney(s) in measurements. ${selectedChimneys} selected.`;
      }
    }

    return null;
  };

  const measurementWarning = getMeasurementWarning();

  return (
    <div className="space-y-6">
      {/* Phase Header */}
      <Card className="border-2">
        <CardHeader className="space-y-4">
          <div className="flex items-start gap-4">
            <div className="flex-shrink-0 w-12 h-12 rounded-full bg-primary/10 flex items-center justify-center">
              <IconComponent className="w-6 h-6 text-primary" />
            </div>
            <div className="flex-1">
              <div className="flex items-center gap-2 mb-2">
                <Badge variant="outline" className="text-xs">
                  Step {phase.number} of 15
                </Badge>
                {(phase as any).isOptional && (
                  <Badge variant="secondary" className="text-xs">
                    Optional
                  </Badge>
                )}
                {(phase as any).isHero && (
                  <Badge className="text-xs bg-gradient-to-r from-primary to-primary/80">
                    ⭐ Key Selection
                  </Badge>
                )}
              </div>
              <h2 className="text-2xl md:text-3xl font-bold mb-2">
                {phase.title}
              </h2>
              <p className="text-muted-foreground">{phase.description}</p>
            </div>
          </div>
        </CardHeader>
        <CardContent className="space-y-3">
          <Alert className="bg-blue-50 dark:bg-blue-950 border-blue-200 dark:border-blue-800">
            <InfoIcon className="w-5 h-5 text-blue-600 dark:text-blue-400" />
            <AlertDescription className="text-blue-900 dark:text-blue-100">
              <strong>Why This Matters:</strong> {phase.customerExplanation}
            </AlertDescription>
          </Alert>

          {measurementWarning && (
            <Alert className="bg-amber-50 dark:bg-amber-950 border-amber-300 dark:border-amber-700">
              <AlertTriangle className="w-5 h-5 text-amber-600 dark:text-amber-400" />
              <AlertDescription className="text-amber-900 dark:text-amber-100">
                {measurementWarning}
              </AlertDescription>
            </Alert>
          )}

          {/* Phase 11.5 specific alerts */}
          {phase.key === 'Phase 11.5: Shingle Color' && !selectedShingleType && (
            <Alert className="bg-amber-50 dark:bg-amber-950 border-amber-300 dark:border-amber-700">
              <AlertCircle className="h-4 w-4 text-amber-600" />
              <AlertTitle>Select Shingle Type First</AlertTitle>
              <AlertDescription>
                Please go back to Phase 11 and select a shingle type before choosing a color.
              </AlertDescription>
            </Alert>
          )}

          {phase.key === 'Phase 11.5: Shingle Color' && selectedShingleType && (
            <Alert className="bg-blue-50 dark:bg-blue-950 border-blue-200 dark:border-blue-800">
              <Info className="h-4 w-4 text-blue-600" />
              <AlertTitle>Showing Colors For: {selectedShingleType.toUpperCase()}</AlertTitle>
              <AlertDescription>
                Select your preferred color or click "Decide Color Later" to continue.
              </AlertDescription>
            </Alert>
          )}
        </CardContent>
      </Card>

      {/* Tier Selector */}
      {showTierSelector && (
        <Card className="border-2 border-primary/20 bg-primary/5">
          <CardContent className="pt-6">
            <TierSelector 
              value={currentTier}
              onChange={handleTierChange}
            />
            
            {/* Price difference indicators */}
            <div className="mt-4 grid grid-cols-3 gap-2 text-xs">
              {(['good', 'better', 'best'] as const).map((tier) => {
                const diff = getPriceDifference(tier);
                const isCurrentTier = tier === currentTier;
                
                return (
                  <div 
                    key={tier}
                    className={cn(
                      "p-2 rounded text-center",
                      isCurrentTier ? "bg-primary/10 border border-primary" : "bg-muted"
                    )}
                  >
                    <div className="font-semibold capitalize mb-1">{tier}</div>
                    {diff !== null && !isCurrentTier && (
                      <div className={diff > 0 ? "text-amber-600" : "text-green-600"}>
                        {diff > 0 ? '+' : ''}{diff.toFixed(0)}
                      </div>
                    )}
                    {isCurrentTier && (
                      <div className="text-primary font-semibold">Selected</div>
                    )}
                  </div>
                );
              })}
            </div>

            {/* Tier comparison info */}
            {tierDefaults && tierDefaults.length > 0 && (
              <div className="mt-4 text-sm text-muted-foreground">
                <p className="font-medium mb-2">Current Selection:</p>
                <div className="bg-background p-3 rounded">
                  {tierDefaults.find(td => td.tier === currentTier)?.product?.name || 'No product selected'}
                </div>
              </div>
            )}
          </CardContent>
        </Card>
      )}

      {/* Product Selection Grid */}
      <div>
        <h3 className="text-lg font-semibold mb-4">
          Select Your Products
        </h3>
        <ProductCardGrid
          products={products}
          productPrices={productPrices}
          selectedProductIds={selectedProductIds}
          onProductSelect={onProductSelect}
          priceBookId={priceBookId}
          measurementVars={measurementVars}
          lineItems={lineItems}
          categoryConfigs={categoryConfigs}
          defaultProductId={defaultProductId}
        />
        
        {/* Decide Color Later button */}
        {phase.key === 'Phase 11.5: Shingle Color' && onSkipColorSelection && (
          <Button 
            variant="outline" 
            onClick={onSkipColorSelection}
            className="w-full mt-4"
          >
            Decide Color Later
          </Button>
        )}
      </div>
    </div>
  );
}
